/**
 * Utility function to copy text to clipboard with fallback.
 * @param text The text to copy.
 * @param onCopySuccess Optional callback run on successful copy.
 */
export const copyToClipboard = async (
  text: string,
  onCopySuccess?: () => void
): Promise<boolean> => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      onCopySuccess?.();
      return true;
    }
  } catch (err) {
    console.error("Failed to copy using clipboard API", err);
  }

  // Fallback for older browsers
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    const successful = document.execCommand("copy");
    if (successful) {
      onCopySuccess?.();
      return true;
    }
    return false;
  } catch (err) {
    console.error("Fallback copy failed", err);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

/**
 * Handle copy link action.
 * @param shareUrl The URL to copy.
 * @param onCopySuccess Optional callback run on successful copy.
 */
export const handleCopy = (
  shareUrl: string,
  onCopySuccess?: () => void
): void => {
  copyToClipboard(shareUrl, onCopySuccess);
};

/**
 * Handle email share action.
 * @param shareTitle The email subject.
 * @param shareText The email body text.
 */
export const handleEmail = (shareTitle: string, shareText: string): void => {
  const subject = encodeURIComponent(shareTitle);
  const body = encodeURIComponent(shareText);
  window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
};

/**
 * Handle WhatsApp share action.
 * @param shareText The text to send over WhatsApp.
 */
export const handleWhatsApp = (shareText: string): void => {
  const waText = encodeURIComponent(shareText);
  window.open(`https://wa.me/?text=${waText}`, "_blank");
};

/**
 * Handle native share API action, with fallback on failure or lack of support.
 * @param shareText Text content for native sharing.
 * @param shareTitle Title for native sharing.
 * @param shareUrl URL for native sharing.
 * @param onFallback Callback on fallback (e.g., to show a custom popover).
 * @param e Optional React MouseEvent to stop propagation.
 */
export const handleShare = async (
  bodyAreaText: string | undefined,
  shareTitle: string,
  shareUrl: string | undefined,
  onFallback: () => void,
  e?: React.MouseEvent
): Promise<void> => {
  e?.stopPropagation();
  if (navigator.share) {
    try {
      await navigator.share({
        text: bodyAreaText,
        title: shareTitle,
        url: shareUrl,
      });
    } catch (err) {
      if (
        typeof err === "object" &&
        err !== null &&
        "name" in err &&
        (err as { name: string }).name !== "AbortError"
      ) {
        onFallback();
      }
    }
  } else {
    onFallback();
  }
};
